import { useState } from 'react';
import { showToast } from '@repo/ui';
import { Button, Card, CardHeader, CardBody } from '@repo/ui';
import { Icon } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';

// Import query hooks
import {
  useClioWebhooks,
  useClioWebhookInspection,
  useCreateClioWebhook,
  useDeleteClioWebhook,
  useRecreateClioWebhook,
  useRecreateAllClioWebhooks,
  useDeleteFromClio,
} from '../queries';

// Import components
import {
  WebhookModelSelector,
  WebhookSubscriptionCard,
  ConfirmActionDialog,
  ClioInspectionModal,
} from '../components';

// Import types
import type { ConfirmDialogState } from '../types';

export function ClioWebhookLifecycleManagerPage() {
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState<ConfirmDialogState>({
    show: false,
    modelName: '',
    action: '',
  });
  const [showClioInspection, setShowClioInspection] = useState(false);

  // Use query hooks
  const { data: webhooks = [], isLoading, refetch } = useClioWebhooks();
  const {
    data: clioInspectionData,
    isLoading: inspectionLoading,
    refetch: refetchInspection,
  } = useClioWebhookInspection(showClioInspection);

  // Use mutation hooks
  const createMutation = useCreateClioWebhook();
  const deleteMutation = useDeleteClioWebhook();
  const recreateMutation = useRecreateClioWebhook();
  const recreateAllMutation = useRecreateAllClioWebhooks();
  const deleteFromClioMutation = useDeleteFromClio();

  const handleCreateWebhook = () => {
    if (!selectedModel || selectedEvents.length === 0) {
      showToast.error('Please select a model and at least one event');
      return;
    }

    createMutation.mutate(
      {
        modelName: selectedModel,
        events: selectedEvents,
      },
      {
        onSuccess: () => {
          setSelectedModel('');
          setSelectedEvents([]);
        },
      }
    );
  };

  const handleDeleteWebhook = (modelName: string) => {
    setShowConfirmDialog({ show: true, modelName, action: 'delete' });
  };

  const handleRecreateWebhook = (modelName: string) => {
    setShowConfirmDialog({ show: true, modelName, action: 'recreate' });
  };

  const confirmAction = () => {
    if (showConfirmDialog.action === 'delete') {
      deleteMutation.mutate(showConfirmDialog.modelName, {
        onSettled: () => setShowConfirmDialog({ show: false, modelName: '', action: '' }),
      });
    } else if (showConfirmDialog.action === 'recreate') {
      recreateMutation.mutate(
        { modelName: showConfirmDialog.modelName },
        {
          onSettled: () => setShowConfirmDialog({ show: false, modelName: '', action: '' }),
        }
      );
    } else if (
      (showConfirmDialog.action === 'delete_clio' || showConfirmDialog.action === 'delete_clio_only') &&
      showConfirmDialog.clioWebhookId
    ) {
      deleteFromClioMutation.mutate(showConfirmDialog.clioWebhookId, {
        onSettled: () => setShowConfirmDialog({ show: false, modelName: '', action: '' }),
      });
    }
  };

  const handleDeleteFromInspection = (webhook: any, action: string) => {
    setShowConfirmDialog({
      show: true,
      modelName: webhook.model,
      action: action,
      clioWebhookId: webhook.id,
    });
  };

  return (
    <PageLayout
      title="Clio Webhook Lifecycle Manager"
      description="Manage Clio webhooks with automatic processing enablement. All webhooks are created with auto-processing enabled by default."
    >
      <div className="clio-webhook-lifecycle-manager max-w-6xl mx-auto p-6">
        {/* Create Webhook Form - Extracted Component */}
      <WebhookModelSelector
        selectedModel={selectedModel}
        selectedEvents={selectedEvents}
        onModelChange={setSelectedModel}
        onEventsChange={setSelectedEvents}
        onCreate={handleCreateWebhook}
        isCreating={createMutation.isPending}
      />

      {/* Webhook List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                Clio Webhooks
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage existing Clio webhook subscriptions
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => setShowClioInspection(true)}
                variant="outline"
                size="sm"
                leftIcon={<Icon name="HiCog" size="sm" />}
              >
                Inspect Clio
              </Button>
              <Button
                onClick={() => refetch()}
                disabled={isLoading}
                variant="outline"
                size="sm"
                leftIcon={<Icon name="HiRefresh" size="sm" />}
              >
                Refresh
              </Button>
              <Button
                onClick={() => recreateAllMutation.mutate()}
                disabled={recreateAllMutation.isPending || webhooks.length === 0}
                loading={recreateAllMutation.isPending}
                variant="secondary"
                size="sm"
                leftIcon={<Icon name="HiRefresh" size="sm" />}
              >
                Recreate All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading webhooks...</p>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="HiExclamation" className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600 dark:text-gray-400">No Clio webhooks configured</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Create your first webhook above
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {webhooks.map(webhook => (
                <WebhookSubscriptionCard
                  key={webhook.modelName}
                  webhook={webhook}
                  onRecreate={handleRecreateWebhook}
                  onDelete={handleDeleteWebhook}
                  isRecreating={recreateMutation.isPending}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Confirmation Dialog - Extracted Component */}
      <ConfirmActionDialog
        dialog={showConfirmDialog}
        onConfirm={confirmAction}
        onCancel={() => setShowConfirmDialog({ show: false, modelName: '', action: '' })}
        isProcessing={
          deleteMutation.isPending || recreateMutation.isPending || deleteFromClioMutation.isPending
        }
      />

      {/* Clio Webhook Inspection Modal - Extracted Component */}
      <ClioInspectionModal
        open={showClioInspection}
        onClose={() => setShowClioInspection(false)}
        inspectionData={clioInspectionData}
        isLoading={inspectionLoading}
        localWebhooks={webhooks}
        onDeleteClick={handleDeleteFromInspection}
        onRefresh={refetchInspection}
      />
      </div>
    </PageLayout>
  );
}
